import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  LinearProgress,
  Card,
  CardContent,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { PhoneCall, Tickets, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { dummyCallLogs, dummySubDispositionChart } from "../../../utils/dummyData";


ChartJS.register(ArcElement, Tooltip, Legend);

const CustomerQueries = ({ data, tickets, ticketType, stats, isDemoMode }) => {

  const token = localStorage.getItem("token");
  const userId = localStorage.getItem("userId");

  const [getCallDetails, setGetCallDetails] = useState([]);
  const [getSubDispositionChart, setSubDispositionChart] = useState({});
  const [isLoadingCalls, setIsLoadingCalls] = useState(true);
  const [isLoadingChart, setIsLoadingChart] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    if (!isDemoMode) {
      fetchTableData();
      getUserCallLogsChart();
    } else {
      setGetCallDetails(dummyCallLogs);
      setSubDispositionChart(dummySubDispositionChart);
      setIsLoadingCalls(false);
      setIsLoadingChart(false);
    }
  }, [isDemoMode]);


  const fetchTableData = async () => {
    setIsLoadingCalls(true);
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllCallLogs}/${userId}`,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const myData = res?.data?.data;
      setGetCallDetails(myData.reverse().slice(0, 6));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoadingCalls(false);
    }
  };

  // ✅ Fetch Sub-Disposition chart data
  const getUserCallLogsChart = async () => {
    setIsLoadingChart(true);
    try {
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getUserCallLogsChart}/${userId}`,
        {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSubDispositionChart(res.data);
      console.log("Sub-Disposition Chart:", res.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoadingChart(false);
    }
  };

  // ✅ Setup chart data dynamically
  const subDispositionLabels = Object.keys(getSubDispositionChart);
  const subDispositionValues = Object.values(getSubDispositionChart);

  // Calculate total and percentages
  const totalDispositions = subDispositionValues.reduce((a, b) => a + b, 0);

  // Color palette
  const colorPalette = [
    { bg: "#4a64aa", light: "#F1F5F9", text: "#1E40AF" }, // Classic Navy
    { bg: "#495566", light: "#F1F5F9", text: "#475569" }, // Professional Slate
    { bg: "#477e6f", light: "#F1F5F9", text: "#059669" }, // Deep Emerald
    { bg: "#913f3f", light: "#F1F5F9", text: "#B91C1C" }, // Classic Crimson
    { bg: "#f59f5d", light: "#F1F5F9", text: "#92400E" }, // Dark Amber/Gold
    { bg: "#764c97", light: "#F1F5F9", text: "#6B21A8" }, // Deep Royal Purple
    { bg: "#418b9f", light: "#F1F5F9", text: "#0891B2" }, // Classic Teal
    { bg: "#5b7091", light: "#F1F5F9", text: "#1F2937" }, // Iron / Dark Grey
    { bg: "#468ea3", light: "#F1F5F9", text: "#164E63" }, // Midnight Blue
    { bg: "#4338CA", light: "#F1F5F9", text: "#3730A3" }, // Classic Indigo
  ];

  const cardStyle = {
    border: "1px solid #EEEEEE",
    borderRadius: 6,
    padding: 3,
  };

  const getStatusBgColor = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "green";
      case "in progress":
        return "orange";
      case "closed":
        return "red";
      case "waiting":
        return "orange";
      case "on-hold":
        return "black";
      default:
        return "blue";
    }
  };

  const formatDate = (dateString) => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sept",
      "Oct",
      "Nov",
      "Dec",
    ];
    const date = new Date(dateString);
    if (isNaN(date)) return "Invalid Date";
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Skeleton Loader for Chart
  const ChartSkeleton = () => (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
      <Skeleton variant="text" width="60%" height={30} sx={{ mb: 2 }} />
      {[1, 2, 3, 4, 5].map((item) => (
        <Box key={item} sx={{ mb: 2 }}>
          <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={8}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      ))}
    </Box>
  );

  // Skeleton Loader for Call Logs
  const CallLogsSkeleton = () => (
    <Box>
      <Skeleton variant="text" width="50%" height={30} sx={{ mb: 1 }} />
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Box
          key={item}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
            <Skeleton
              variant="circular"
              width={20}
              height={20}
              sx={{ mr: 1 }}
            />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="95%" height={18} />
              <Skeleton variant="text" width="60%" height={12} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  // ✅ NEW: Modern Disposition Overview Component
  const DispositionOverview = () => (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        height: 295,
      }}
    >
      {/* Disposition Items with Progress Bars */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
        {subDispositionLabels.map((label, index) => {
          const value = subDispositionValues[index];
          const percentage =
            totalDispositions > 0
              ? ((value / totalDispositions) * 100).toFixed(1)
              : 0;
          const color = colorPalette[index % colorPalette.length];

          return (
            <Box
              key={index}
              sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}
            >
              {/* Label and Value */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: "#374151",
                    flex: 1,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    minWidth: "80px",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      // fontWeight: 700,
                      color: color.text,
                      fontSize: "12px",
                      textAlign: "right",
                      minWidth: "45px",
                    }}
                  >
                    {value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      // fontWeight: 600,
                      color: "#9CA3AF",
                      fontSize: "10px",
                      textAlign: "right",
                      minWidth: "35px",
                    }}
                  >
                    {percentage}%
                  </Typography>
                </Box>
              </Box>

              {/* Progress Bar */}
              <LinearProgress
                variant="determinate"
                value={parseFloat(percentage)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: color.light,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: color.bg,
                    borderRadius: 3,
                    transition: "all 0.3s ease",
                  },
                  "&:hover .MuiLinearProgress-bar": {
                    boxShadow: `0 0 8px ${color.bg}`,
                  },
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ padding: 0, mt: 2 }}>
      <Grid container spacing={2}>
        {/* ✅ NEW: Modern Disposition Overview */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              ...cardStyle,
              height: 293,
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                display: "none",
              },

              msOverflowStyle: "none",
              scrollbarWidth: "none",
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            {isLoadingChart ? (
              <ChartSkeleton />
            ) : !subDispositionLabels.length ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: 295,
                }}
              >
                <Typography color="grey">
                  No Sub-Disposition data available.
                </Typography>
              </Box>
            ) : (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 550,
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  Disposition Overview
                </Typography>

                <Box
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },

                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                  }}
                >
                  <DispositionOverview />
                </Box>
              </>
            )}
          </Box>
        </Grid>

        {/* ✅ WhatsApp Leads Section */}
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: { xs: "none", md: "initial" },
          }}
        >
          <Grid container>
            <Grid item xs={12}>
              <Box
                sx={{
                  borderRadius: 6,
                  border: "1px solid #EEEEEE",
                  p: 3,
                  bgcolor: "#6366f1",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 4,
                  }}
                >
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 1,
                    }}
                  >
                    <WhatsAppIcon size={20} sx={{ color: "white" }} />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 550,
                      color: "white",
                      flexGrow: 1,
                      ml: 1,
                    }}
                  >
                    WhatsApp Leads
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 2,
                  }}
                >
                  {["Submitted", "Sent", "Delivered", "Read", "Failed"].map(
                    (label, i) => (
                      <Box key={i}>
                        <Typography
                          sx={{
                            mb: 1,
                            fontSize: "small",
                            whiteSpace: "nowrap",
                            color: "white",
                          }}
                        >
                          {label}
                        </Typography>
                        <Typography
                          sx={{ fontWeight: "small", color: "white" }}
                        >
                          0
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            </Grid>

            {/* ✅ Ticket Section */}
            <Grid item xs={12}>
              <Box
                sx={{
                  borderRadius: 6,
                  border: "1px solid #EEEEEE",
                  p: 3,
                  mt: 2,
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Tickets size={20} style={{ color: "#6366f1" }} />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 500, color: "black", flexGrow: 1 }}
                  >
                    Ticket Details
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 3,
                  }}
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 1, fontSize: "small" }}
                    >
                      All Tickets
                    </Typography>
                    <Typography sx={{ fontWeight: "small" }}>
                      {tickets?.length || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 1, fontSize: "small" }}
                    >
                      Pending
                    </Typography>
                    <Typography sx={{ fontWeight: "small" }}>
                      {tickets.length - stats?.resolved || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 1, fontSize: "small" }}
                    >
                      Resolved
                    </Typography>
                    <Typography sx={{ fontWeight: "small" }}>
                      {stats?.resolved || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>

        {/* ✅ Recent Call Logs Section */}
        <Grid item xs={12} md={4} overflow={"hidden"}>
          <Box
            sx={{
              border: "1px solid #EEEEEE",
              borderRadius: 6,
              padding: "16px 25px 0 25px",
              overflow: "auto",
              height: 322,
              "&:hover": {
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            {isLoadingCalls ? (
              <CallLogsSkeleton />
            ) : (
              <>
                {getCallDetails?.length > 0 && (
                  <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 550, flexGrow: 1, ml: 1 }}
                    >
                      Recent Calls Logs
                    </Typography>
                  </Box>
                )}

                <Box mt={1}>
                  {getCallDetails?.length === 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: { xs: 100, md: 250 },
                      }}
                    >
                      <Typography color="grey">
                        No Call logs available.
                      </Typography>
                    </Box>
                  ) : (
                    getCallDetails.map((e, i) => (
                      <Box
                        key={e._id || i}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 1,
                        }}
                      >
                        <Box
                          onClick={() => navigate(`/call-log`)}
                          sx={{
                            display: "flex",
                            cursor: "pointer",
                            alignItems: "center",
                          }}
                        >
                          <PhoneCall
                            size={20}
                            style={{ color: "#2b8aff", marginRight: "8px" }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: "small" }}>
                              {e?.payload?.calls_agent_number}
                            </Typography>
                            <Typography
                              sx={{ fontSize: "small", color: "grey" }}
                            >
                              Agent : {e?.payload.user}
                            </Typography>
                          </Box>
                        </Box>

                        <Box>
                          <Typography
                            sx={{
                              color: "grey",
                              fontSize: "small",
                              textAlign: "right",
                            }}
                          >
                            {formatDate(e.payload.callDate)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "small",
                              color: getStatusBgColor(e.status || e.payload?.status),
                              textAlign: "right",
                            }}
                          >
                            {e.status || e.payload?.status}
                          </Typography>
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerQueries;
